import { useState, useEffect, useMemo } from 'react';

/**
 * Hook to detect mobile devices and screen size efficiently
 * Cached and throttled to avoid performance drops on resize
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkMobile = () => {
      const screenCheck = window.innerWidth < 768; // Tailwind md breakpoint
      const userAgent = navigator.userAgent || navigator.vendor || '';
      const userAgentCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
      setIsMobile(screenCheck || userAgentCheck);
    };

    // Throttle resize events to prevent spamming
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 200);
    };

    checkMobile();
    window.addEventListener('resize', handleResize);

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return isMobile;
};

/**
 * Hook to detect user's motion preference
 * Cached for better performance
 */
export const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook to determine if animations should be reduced
 * Combines mobile detection and system preferences
 */
export const useShouldReduceAnimations = () => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Memoized return to prevent unnecessary re-renders
  return useMemo(() => isMobile || prefersReducedMotion, [isMobile, prefersReducedMotion]);
};
